import { getDfmSelectionsString } from '../models/quotes/getFeedbackSelections.js';

const calculate = ({ quote, options }) => {
  const {
    cappingStyle,
    quantity,
    delivery,
  } = options;

  const dynamicPrice = ({
    minPrice, maxPrice, minQty, maxQty, qty,
  }) => {
    const slope = (minPrice - maxPrice) / (maxQty - minQty);
    return maxPrice + (qty - minQty) * slope;
  };

  const {
    multiplier,
    leadtimeText,
    leadtimeDays,
    minQuantity,
    maxQuantity,
  } = (quote?.pricingV3?.values?.leadtimes || []).find((l) => l.id === delivery) || {};
  if (!multiplier || (!leadtimeText && !leadtimeDays)) {
    return {};
  }
  if (quantity > maxQuantity || quantity < minQuantity) {
    return {};
  }
  if (cappingStyle === 'placeholder-item') {
    return {};
  }

  const isCapped = !(cappingStyle === 'uncapped');
  // Cart items snapshot the customer's selections at add-to-cart time so two
  // configurations of the same part can coexist; items without a snapshot
  // (or direct quote pricing) fall back to the quote's live selections.
  const feedbackResponses = options.feedbackResponses ?? quote.feedbackResponses ?? {};
  // The reorder setup fee is derived, never written back: re-ordering a DFM
  // configuration (e.g. corner radius) recorded in orderedDfmSelections gets the
  // reduced fee, while a configuration that has never been ordered pays the full
  // calculated setup. Quotes ordered before configurations were tracked had their
  // calculated values overwritten with the reduced fee, so they keep the discount.
  const { calculatedValues } = quote.pricingV3.values;
  const orderedSelections = quote.orderedDfmSelections;
  const isReorderedConfiguration = Array.isArray(orderedSelections)
    && orderedSelections.includes(getDfmSelectionsString(feedbackResponses));
  const baseSetupPrice = isCapped
    ? calculatedValues.cappedSetupPrice
    : calculatedValues.uncappedSetupPrice;
  const setupPrice = Number(
    (
      (isReorderedConfiguration
        ? quote.pricingV3.values.reorderSetup ?? 500
        : baseSetupPrice
      )
       * multiplier
    ).toFixed(2),
  );

  // make sure pricing tiers are sorted
  const pricingLevels = [
    ...quote.pricingV3.values.breakpoints,
  ].sort((a, b) => a.quantity - b.quantity);
  let baseUnitPrice;
  for (let i = 0; i < pricingLevels.length - 1; i += 1) {
    const lowerBound = pricingLevels[i];
    const upperBound = pricingLevels[i + 1];

    if (quantity >= lowerBound.quantity && quantity <= upperBound.quantity) {
      baseUnitPrice = dynamicPrice({
        maxPrice: lowerBound.multiplier,
        minPrice: upperBound.multiplier,
        minQty: lowerBound.quantity,
        maxQty: upperBound.quantity,
        qty: quantity,
      }) * (isCapped
        ? quote.pricingV3.values.calculatedValues.cappedUnitPrice
        : quote.pricingV3.values.calculatedValues.uncappedUnitPrice);
    }
  }

  const unitPrice = Number(
    (multiplier * baseUnitPrice + (isCapped
      ? quote.pricingV3.values.calculatedValues.cappedUnscaledUnitPrice || 0
      : quote.pricingV3.values.calculatedValues.uncappedUnscaledUnitPrice || 0)
    ).toFixed(2),
  );

  // Find and apply any option discounts/surcharges. They adjust the setup for
  // configuration-specific tooling, which was paid with the original order — a
  // re-order of an already-ordered configuration pays the flat reorder setup
  // with no adjustment, so skip them entirely.
  let discount = 0;
  if (!isReorderedConfiguration) {
    const optionalFeedback = quote.optionalFeedback || [];
    for (let i = 0; i < optionalFeedback.length; i += 1) {
      const feedback = optionalFeedback[i];
      const response = feedbackResponses[feedback.title];
      if (response) {
        const option = feedback.options.find((o) => o.value === response);
        if (option) {
          discount += option.discount || 0;
        }
      }
    }
    discount *= multiplier;
  }

  if (leadtimeText) {
    return {
      leadtimeText, setupPrice, unitPrice, discount,
    };
  }
  return {
    leadtimeDays, setupPrice, unitPrice, discount,
  };
};

export default calculate;
